import type { ReactNode } from "react";
import { useEffect } from "react";
import { X } from "lucide-react";
import { Dialog, Heading, ModalOverlay, Modal as RACModal } from "react-aria-components";
import { cn, tv } from "tailwind-variants";

const COOKIE_BANNER_ID = "iubenda-cs-banner";

const overlayStyles = tv({
  base: "absolute top-0 left-0 w-full h-(--page-height) isolate z-30 bg-black/[50%] text-center",
  variants: {
    isEntering: {
      true: "animate-in fade-in duration-200 ease-out"
    },
    isExiting: {
      true: "animate-out fade-out duration-200 ease-in"
    }
  }
});

const modalStyles = tv({
  base: "font-sans w-full rounded-2xl bg-white dark:bg-neutral-800/70 dark:backdrop-blur-2xl dark:backdrop-saturate-200 forced-colors:bg-[Canvas] text-left align-middle text-neutral-700 dark:text-neutral-300 shadow-2xl bg-clip-padding border border-black/10 dark:border-white/10 overflow-hidden",
  variants: {
    isEntering: {
      true: "animate-in zoom-in-105 ease-out duration-200"
    },
    isExiting: {
      true: "animate-out zoom-out-95 ease-in duration-200"
    }
  }
});

export type ModalProps = {
  isOpen: boolean;
  onClose?: () => void;
  children: ReactNode;
  className?: string;
  backdropClassName?: string;
  closeOnEsc?: boolean;
  closeOnBackdropClick?: boolean;
  title?: string;
  description?: string;
};

export function Modal({
  isOpen,
  onClose,
  children,
  className,
  backdropClassName,
  closeOnEsc = true,
  closeOnBackdropClick = true,
  title = "",
  description = ""
}: ModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleOpenChange = (open: boolean) => {
    if (!open && onClose) {
      onClose();
    }
  };

  const isDismissable = closeOnEsc && closeOnBackdropClick;

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      isDismissable={isDismissable}
      className={cn(overlayStyles(), backdropClassName)!}
      // Don't close modal when interacting with the cookie banner
      shouldCloseOnInteractOutside={$el => !$el.closest(`#${COOKIE_BANNER_ID}`)}
    >
      <div className="h-(--visual-viewport-height) sticky left-0 top-0 box-border flex w-full items-center justify-center">
        <RACModal className={cn(modalStyles(), className)!}>
          <Dialog className="flex h-full flex-col">
            {title && (
              <Heading slot="title" className="sr-only">
                {title}
              </Heading>
            )}
            {description && <p className="sr-only">{description}</p>}
            {children}
          </Dialog>
        </RACModal>
      </div>
    </ModalOverlay>
  );
}

export const ModalHeader = ({
  children,
  className,
  onClose
}: {
  children: ReactNode;
  className?: string;
  onClose?: () => void;
}) => (
  <div className={cn("relative flex items-center justify-center border-b p-6", className)}>
    <div className="text-lg font-semibold">{children}</div>
    {onClose && (
      <div className="absolute right-6 flex">
        <button
          type="button"
          data-testid="modal-close-button"
          onClick={onClose}
          className="focus-visible:ring-ring cursor-pointer text-gray-500 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          aria-label="Close"
        >
          <X className="h-6 w-6" />
        </button>
      </div>
    )}
  </div>
);

export const ModalBody = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={cn("overflow-auto p-4", className)}>{children}</div>
);

export const ModalFooter = ({
  children,
  className
}: {
  children: ReactNode;
  className?: string;
}) => <div className={cn("border-t p-4", className)}>{children}</div>;
